/**
 * New Tileset dialog (engine 46.2 flow). Choose an atlas image — pick an
 * already-uploaded project asset, or upload a file — then set tile
 * width/height/spacing/margin over a LIVE slicing preview (the atlas with the
 * grid overlaid, so wrong numbers are immediately obvious). Create → POST
 * /tilesets (the server derives columns/tile_count from the image).
 */

import { useEffect, useRef, useState } from 'react';
import { AssetPicker } from '../../graph/panels/AssetPicker';
import { tilemapClient } from '../api/tilemapClient';

export function NewTilesetDialog({
    projectUuid,
    onClose,
    onCreated,
}: {
    projectUuid: string;
    onClose: () => void;
    onCreated: (tilesetUuid: string) => void;
}) {
    const [assetUuid, setAssetUuid] = useState<string | null>(null);
    const [name, setName] = useState('New Tileset');
    const [tileW, setTileW] = useState(16);
    const [tileH, setTileH] = useState(16);
    const [spacing, setSpacing] = useState(0);
    const [margin, setMargin] = useState(0);
    const [picking, setPicking] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [img, setImg] = useState<HTMLImageElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    // Load the chosen atlas for the preview.
    useEffect(() => {
        if (!assetUuid) { setImg(null); return; }
        const image = new Image();
        image.onload = () => setImg(image);
        image.onerror = () => setError('Could not load the selected image.');
        image.src = `/api/assets/${assetUuid}`;
    }, [assetUuid]);

    // Draw the slicing preview: atlas + grid lines at the current tile geometry.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !img) return;
        const maxW = 420;
        const scale = Math.min(1, maxW / img.width);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        const ctx = canvas.getContext('2d')!;
        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const stepX = (tileW + spacing) * scale;
        const stepY = (tileH + spacing) * scale;
        const m = margin * scale;
        if (stepX > 1 && stepY > 1) {
            ctx.strokeStyle = 'rgba(245,121,59,0.9)';
            ctx.lineWidth = 1;
            for (let x = m; x <= canvas.width + 0.5; x += stepX) line(ctx, x + 0.5, 0, x + 0.5, canvas.height);
            for (let y = m; y <= canvas.height + 0.5; y += stepY) line(ctx, 0, y + 0.5, canvas.width, y + 0.5);
        }
    }, [img, tileW, tileH, spacing, margin]);

    const create = async () => {
        if (!assetUuid) { setError('Choose an atlas image first.'); return; }
        setBusy(true);
        setError(null);
        try {
            const ts = await tilemapClient.createTileset(projectUuid, {
                name: name.trim() || 'New Tileset',
                texture_asset_uuid: assetUuid,
                tile_width: tileW,
                tile_height: tileH,
                spacing,
                margin,
            });
            onCreated(ts.uuid);
        } catch (e) {
            setError((e as Error).message);
            setBusy(false);
        }
    };

    const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setBusy(true);
        try {
            const asset = await tilemapClient.uploadImage(projectUuid, file);
            setAssetUuid(asset.uuid);
            if (name === 'New Tileset') setName(asset.name);
        } catch (err) {
            setError((err as Error).message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="modal modal-blur show d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">New Tileset</h5>
                        <button type="button" className="btn-close" onClick={onClose} />
                    </div>
                    <div className="modal-body">
                        {error && <div className="alert alert-danger py-2">{error}</div>}
                        <div className="row g-3">
                            <div className="col-5">
                                <label className="form-label small">Name</label>
                                <input className="form-control form-control-sm mb-2" value={name} onChange={(e) => setName(e.target.value)} />

                                <label className="form-label small">Atlas image</label>
                                <div className="d-flex gap-2 mb-2">
                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setPicking(true)}>Pick asset</button>
                                    <label className="btn btn-sm btn-outline-secondary mb-0">
                                        Upload
                                        <input type="file" accept="image/*" hidden onChange={onUpload} />
                                    </label>
                                </div>

                                <div className="row g-2">
                                    <NumField label="Tile W" value={tileW} onChange={setTileW} />
                                    <NumField label="Tile H" value={tileH} onChange={setTileH} />
                                    <NumField label="Spacing" value={spacing} onChange={setSpacing} min={0} />
                                    <NumField label="Margin" value={margin} onChange={setMargin} min={0} />
                                </div>
                            </div>
                            <div className="col-7">
                                <label className="form-label small">Slicing preview</label>
                                <div className="border rounded p-2 d-flex align-items-center justify-content-center" style={{ minHeight: 200, background: 'var(--tblr-bg-surface-secondary)' }}>
                                    {img ? (
                                        <canvas ref={canvasRef} style={{ imageRendering: 'pixelated', maxWidth: '100%' }} />
                                    ) : (
                                        <span className="text-secondary small">Choose an image to preview slicing.</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button className="btn btn-sm btn-link" onClick={onClose}>Cancel</button>
                        <button className="btn btn-sm btn-primary" disabled={busy || !assetUuid} onClick={create}>
                            {busy ? 'Working…' : 'Create tileset'}
                        </button>
                    </div>
                </div>
            </div>

            {picking && (
                <AssetPicker
                    kind="image"
                    onSelect={(uuid) => setAssetUuid(uuid)}
                    onCancel={() => setPicking(false)}
                />
            )}
        </div>
    );
}

function NumField({ label, value, onChange, min = 1 }: { label: string; value: number; onChange: (n: number) => void; min?: number }) {
    return (
        <div className="col-6">
            <label className="form-label small mb-0">{label}</label>
            <input
                type="number"
                min={min}
                className="form-control form-control-sm"
                value={value}
                onChange={(e) => onChange(Math.max(min, Number(e.target.value) || min))}
            />
        </div>
    );
}

function line(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number) {
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.stroke();
}
